import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Users, BookOpen, MessageSquare, Trophy, Check, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Footer } from "@/components/Footer";
import { JobsPreviewTeaser } from "@/components/jobs/JobsPreviewTeaser";
import { RegisterDialog } from "@/components/auth/RegisterDialog";
import { usePageMeta } from "@/hooks/usePageMeta";
import { CommunitySchema } from "@/components/seo/CommunitySchema";

export default function IndexPreview() {
  usePageMeta({
    title: "Vagas em Dados 2026 | Analista, Engenheiro e Cientista de Dados",
    description: "Encontre vagas curadas em análise, engenharia e ciência de dados. Comunidade gratuita com +1000 profissionais. Cadastre-se e acelere sua carreira em dados!",
    canonical: "https://datahireclub.com.br",
    ogImage: "https://datahireclub.com.br/og-image.png",
  });

  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    fullName: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userData = {
        full_name: formData.fullName,
        user_type: "candidate",
      };

      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: userData,
          emailRedirectTo: `${window.location.origin}/login`,
        }
      });

      if (error) throw error;

      await new Promise(resolve => setTimeout(resolve, 1500));

      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          full_name: formData.fullName,
          user_type: "candidate",
          email: formData.email,
        })
        .eq('id', data.user?.id);

      if (profileError) throw profileError;

      if (data) {

        try {

          const { data: brevoData, error: brevoError } = await supabase.functions.invoke('create-brevo-contact', {
            body: {
              email: formData.email,
              firstName: formData.fullName,
              lastName: "",
              userId: data.user?.id,
              userType: "candidate",
            },
          });

          if (brevoError) {
            console.error('Brevo API error:', brevoError);
          } else {
            console.log('Brevo contact created successfully:', brevoData);
          }
        } catch (brevoError) {
          console.error('Failed to create Brevo contact:', brevoError);
        }

        setRegisteredEmail(formData.email);
        setShowEmailModal(true);
      }
    } catch (error: unknown) {
      toast({
        title: "Erro no cadastro",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CommunitySchema />
      {/* Navigation - Fora do container com overflow para manter sticky */}
      <nav className="border-b bg-black text-white sticky top-0 z-50">
        <div className="container mx-auto px-3 md:px-4 h-14 md:h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img
              src="/lovable-uploads/logo-horizontal.png"
              alt="Data Hire Club - Vagas e comunidade para profissionais de dados"
              className="h-8 md:h-10 w-auto"
              width="160"
              height="40"
              loading="eager"
              fetchPriority="high"
              decoding="sync"
            />
          </Link>
          <div className="flex items-center gap-2 md:gap-4">
            <Link to="/login" className="text-xs md:text-sm text-gray-300 hover:text-white">
              Já tem conta? Entre
            </Link>
            <Link to="/para-empresas">
              <Button variant="outline" size="sm" className="text-black border-gray-300 bg-white hover:bg-primary hover:text-white hover:border-primary px-3 py-1 text-xs md:text-sm">
                Sou Empresa
              </Button>
            </Link>
          </div>
        </div>
      </nav>
      
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 overflow-x-hidden">

      {/* Hero Section */}
      <section className="container mx-auto px-3 sm:px-4 py-8 sm:py-12 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-12 items-start">
          <div className="space-y-6 min-w-0">
            <div className="space-y-4">
              <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold leading-tight">
                Sua Carreira em{" "}
                <span className="text-primary">Dados</span>{" "}
                Começa Aqui
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-muted-foreground">
                Junte-se <strong>GRATUITAMENTE</strong> à maior comunidade de profissionais de dados do Brasil
              </p>
            </div>

            <div className="flex flex-wrap gap-2 sm:gap-4 max-w-full">
              <div className="flex items-center gap-2 bg-primary/10 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full">
                <Check className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
                <span className="text-xs sm:text-sm font-medium whitespace-nowrap">100% Gratuito</span>
              </div>
              <div className="flex items-center gap-2 bg-primary/10 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full">
                <Users className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
                <span className="text-xs sm:text-sm font-medium whitespace-nowrap">Comunidade Ativa</span>
              </div>
              <div className="flex items-center gap-2 bg-primary/10 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full">
                <Trophy className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
                <span className="text-xs sm:text-sm font-medium whitespace-nowrap">Sistema de Gamificação</span>
              </div>
            </div>

            {/* Jobs Preview Teaser */}
            <div className="mt-8">
              <JobsPreviewTeaser onRegisterClick={() => setShowRegisterDialog(true)} />
            </div>
          </div>

          {/* Registration Form */}
          <Card id="cadastro" className="hidden lg:block bg-background/80 backdrop-blur-sm border-2 shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl text-center">Cadastre-se Grátis</CardTitle>
              <CardDescription className="text-center">
                Comece sua jornada em 30 segundos
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="fullName">Nome completo</Label>
                  <Input
                    id="fullName"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    placeholder="Seu nome completo"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="seu@email.com"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Senha</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                  size="lg"
                >
                  {loading ? "Cadastrando..." : "Cadastrar Grátis"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <p className="text-xs text-center text-muted-foreground">
                  Ao se cadastrar, você concorda com nossos{" "}
                  <Link to="/termos" className="text-primary hover:underline">
                    Termos de Uso
                  </Link>
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="bg-muted/30 py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Por que Escolher o Data Hire Club?</h2>
            <p className="text-xl text-muted-foreground">
              Tudo que você precisa para acelerar sua carreira em dados
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="text-center p-6">
              <div className="w-12 h-12 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center">
                <Check className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Gratuito para Sempre</h3>
              <p className="text-muted-foreground">
                Sem taxas ocultas, sem pegadinhas. Acesso completo à plataforma
              </p>
            </Card>

            <Card className="text-center p-6">
              <div className="w-12 h-12 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Comunidade Ativa</h3>
              <p className="text-muted-foreground">
                Conecte-se com analistas, cientistas e engenheiros de dados
              </p>
            </Card>


            <Card className="text-center p-6">
              <div className="w-12 h-12 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center">
                <Trophy className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Gamificação</h3>
              <p className="text-muted-foreground">
                Ganhe pontos e níveis por participação ativa na comunidade
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Community Features */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Funcionalidades da Comunidade</h2>
            <p className="text-xl text-muted-foreground">
              Conecte-se, aprenda e cresça com outros profissionais
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-6">
              <div className="w-12 h-12 mb-4 bg-primary/10 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Apresente-se à Comunidade</h3>
              <p className="text-muted-foreground mb-4">
                Compartilhe sua jornada profissional e conecte-se com outros membros da comunidade
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary flex-shrink-0" />
                  Conte sua história profissional
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary flex-shrink-0" />
                  Encontre profissionais similares
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary flex-shrink-0" />
                  Expanda sua rede de contatos
                </li>
              </ul>
            </Card>

            <Card className="p-6">
              <div className="w-12 h-12 mb-4 bg-primary/10 rounded-lg flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Acelere seu Aprendizado</h3>
              <p className="text-muted-foreground mb-4">
                Descubra cursos, livros e recursos compartilhados pela comunidade
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary flex-shrink-0" />
                  Recursos curados por experts
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary flex-shrink-0" />
                  Discussões sobre tendências
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary flex-shrink-0" />
                  Compartilhe seus aprendizados
                </li>
              </ul>
            </Card>

            <Card className="p-6">
              <div className="w-12 h-12 mb-4 bg-primary/10 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Tire suas Dúvidas</h3>
              <p className="text-muted-foreground mb-4">
                Receba ajuda de profissionais experientes em tempo real
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary flex-shrink-0" />
                  Suporte da comunidade
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary flex-shrink-0" />
                  Respostas rápidas
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary flex-shrink-0" />
                  Sistema de busca avançado
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="bg-muted/30 py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Como Funciona</h2>
            <p className="text-xl text-muted-foreground">
              Em 5 passos simples, você está pronto para acelerar sua carreira
            </p>
          </div>

          <div className="grid md:grid-cols-5 gap-6">
            {[
              { number: "1", title: "Cadastro Gratuito", desc: "30 segundos para criar sua conta" },
              { number: "2", title: "Complete seu Perfil", desc: "Adicione skills e experiência" },
              { number: "3", title: "Explore a Comunidade", desc: "Participe das discussões" },
              { number: "4", title: "Candidate-se a Vagas", desc: "Acesse oportunidades exclusivas" },
              { number: "5", title: "Ganhe Pontos", desc: "Por participação ativa" }
            ].map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 mx-auto mb-4 bg-primary rounded-full flex items-center justify-center text-white font-bold text-lg">
                  {step.number}
                </div>
                <h3 className="font-semibold mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Categories Section */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Áreas de Dados Mais Procuradas</h2>
            <p className="text-xl text-muted-foreground">
              Encontre vagas nas principais carreiras de dados
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Card className="p-6 cursor-pointer" onClick={() => setShowRegisterDialog(true)}>
              <div className="w-12 h-12 mb-4 bg-primary/10 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-primary">Análise de Dados</h3>
              <p className="text-muted-foreground mb-4">
                Analista de Dados, BI, Analytics, Data Analyst
              </p>
              <Button variant="link" className="text-primary p-0 h-auto font-medium no-underline">
                Ver vagas →
              </Button>
            </Card>
            
            <Card className="p-6 cursor-pointer" onClick={() => setShowRegisterDialog(true)}>
              <div className="w-12 h-12 mb-4 bg-primary/10 rounded-lg flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-primary">Engenharia de Dados</h3>
              <p className="text-muted-foreground mb-4">
                Data Engineer, ETL, Pipelines, DataOps
              </p>
              <Button variant="link" className="text-primary p-0 h-auto font-medium no-underline">
                Ver vagas →
              </Button>
            </Card>
            
            <Card className="p-6 cursor-pointer" onClick={() => setShowRegisterDialog(true)}>
              <div className="w-12 h-12 mb-4 bg-primary/10 rounded-lg flex items-center justify-center">
                <Trophy className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-primary">Ciência de Dados</h3>
              <p className="text-muted-foreground mb-4">
                Data Scientist, ML, AI, Machine Learning
              </p>
              <Button variant="link" className="text-primary p-0 h-auto font-medium no-underline">
                Ver vagas →
              </Button>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Perguntas Frequentes</h2>
            <p className="text-xl text-muted-foreground">
              Tudo que você precisa saber sobre o Data Hire Club
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto space-y-4">
            <details className="bg-muted/30 p-6 rounded-lg group">
              <summary className="font-semibold cursor-pointer text-lg list-none flex items-center justify-between">
                O que é o Data Hire Club?
                <span className="ml-2 transform group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                Somos uma plataforma gratuita que conecta profissionais de dados com as melhores oportunidades do mercado. Combinamos vagas curadas com uma comunidade ativa para você crescer na carreira.
              </p>
            </details>
            
            <details className="bg-muted/30 p-6 rounded-lg group">
              <summary className="font-semibold cursor-pointer text-lg list-none flex items-center justify-between">
                Como funciona a candidatura às vagas?
                <span className="ml-2 transform group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                Simples! Encontre a vaga ideal navegando pela nossa lista curada, clique em "Ver vaga" para conhecer todos os detalhes e depois em "Quero me candidatar" para ser direcionado ao processo seletivo da empresa.
              </p>
            </details>
            
            <details className="bg-muted/30 p-6 rounded-lg group">
              <summary className="font-semibold cursor-pointer text-lg list-none flex items-center justify-between">
                Preciso pagar para usar a plataforma?
                <span className="ml-2 transform group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                Não! A plataforma é 100% gratuita para candidatos. Você tem acesso ilimitado a todas as vagas, recursos da comunidade, sistema de gamificação e networking. Sem pegadinhas ou taxas ocultas.
              </p>
            </details>
            
            <details className="bg-muted/30 p-6 rounded-lg group">
              <summary className="font-semibold cursor-pointer text-lg list-none flex items-center justify-between">
                Que tipos de vagas vocês publicam?
                <span className="ml-2 transform group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                Focamos exclusivamente em carreiras de dados: Analista de Dados, Engenheiro de Dados, Cientista de Dados, BI, Analytics, Data Ops e áreas relacionadas. Todas as vagas são cuidadosamente curadas pela nossa equipe.
              </p>
            </details>
            
            <details className="bg-muted/30 p-6 rounded-lg group">
              <summary className="font-semibold cursor-pointer text-lg list-none flex items-center justify-between">
                O que é o sistema de gamificação?
                <span className="ml-2 transform group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                Nossa gamificação recompensa sua participação ativa na comunidade. Ganhe pontos e suba de nível ao se apresentar, compartilhar conhecimento, ajudar outros membros e participar das discussões. Quanto mais ativo, mais destaque você ganha!
              </p>
            </details>
            
            <details className="bg-muted/30 p-6 rounded-lg group">
              <summary className="font-semibold cursor-pointer text-lg list-none flex items-center justify-between">
                Como funciona a comunidade?
                <span className="ml-2 transform group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                Nossa comunidade é dividida em três áreas principais: Apresentações (conheça outros profissionais), Aprendizado (compartilhe e descubra recursos) e Perguntas (tire dúvidas e ajude outros). É um espaço seguro para crescer profissionalmente.
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary/5">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold mb-4">Pronto para Começar?</h2>
            <p className="text-xl text-muted-foreground mb-8">
              Cadastre-se gratuitamente e comece a acelerar sua carreira em dados hoje mesmo
            </p>
            <Button
              size="lg"
              className="text-lg px-8"
              onClick={() => {
                document.getElementById('cadastro')?.scrollIntoView({
                  behavior: 'smooth'
                });
              }}
            >
              Cadastrar Agora - É Grátis
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <p className="text-sm text-muted-foreground mt-4">
              Sem compromisso • Acesso completo • Para sempre gratuito
            </p>
          </div>
        </div>
      </section>
      <Footer />
      
      {/* Register Dialog */}
      <RegisterDialog 
        open={showRegisterDialog} 
        onOpenChange={setShowRegisterDialog}
      />
      
      <AlertDialog open={showEmailModal} onOpenChange={() => { }}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Mail className="w-8 h-8 text-primary" />
              </div>
            </div>
            <AlertDialogTitle className="text-xl">📧 Verifique seu Email!</AlertDialogTitle>
            <AlertDialogDescription className="text-left space-y-3">
              <p>Enviamos um email de confirmação para:</p>
              <div className="bg-muted p-3 rounded-lg text-center">
                <strong className="text-primary">{registeredEmail}</strong>
              </div>
              <div className="space-y-2">
                <p className="font-medium">Para completar seu cadastro:</p>
                <ol className="list-decimal list-inside space-y-1 text-sm">
                  <li>Acesse sua caixa de entrada</li>
                  <li>Procure por email do <strong>Data Hire Club</strong></li>
                  <li>Clique no link de confirmação</li>
                  <li>Volte aqui e faça login!</li>
                </ol>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg">
                <p className="text-sm text-yellow-800">
                  ⚠️ Não esqueça de verificar a pasta de <strong>spam/lixo eletrônico</strong>
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogAction
            className="w-full"
            onClick={() => {
              setShowEmailModal(false);
              navigate('/login');
            }}
          >
            Entendi, vou verificar!
          </AlertDialogAction>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </>
  );
}
